import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2 } from "lucide-react";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || "a6cf0518-295e-4c3e-882e-d32aaa671c7a";

const QUANTITY_OPTIONS = [
  "50 - 100",
  "100 - 250",
  "250 - 500",
  "500 - 1000",
  "1000 - 2000",
  "2000+",
];

const CATEGORY_OPTIONS = [
  "Dated Diaries",
  "Journals",
  "Customised Notebooks",
  "Gold Foiling",
  "Page Insertions",
  "Fully Customised Diaries",
];

const inputClass =
  "w-full bg-transparent border border-black/10 rounded-lg px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#7A1A31] focus:ring-1 focus:ring-[#7A1A31]/20 transition-all";
const inputClassDark =
  "w-full bg-white/[0.07] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#C8A23A] focus:ring-1 focus:ring-[#C8A23A]/20 transition-all";
const labelClass = "block text-xs tracking-wide font-medium text-[#6B7280] mb-1.5";
const labelClassDark = "block text-xs tracking-wide font-medium text-white/50 mb-1.5";

export const InquiryForm = ({ source = "home", variant = "light", compact = false }) => {
  const dark = variant === "dark";
  const ic = dark ? inputClassDark : inputClass;
  const lc = dark ? labelClassDark : labelClass;

  const [form, setForm] = useState({
    name: "", phone: "", email: "", company: "",
    location: "", designation: "", quantity: "", category: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Eagle Diaries Inquiry — ${source}`,
          ...form,
          source,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed");
      setStatus("success");
      setForm({ name: "", phone: "", email: "", company: "", location: "", designation: "", quantity: "", category: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`flex flex-col items-center justify-center py-16 text-center ${compact ? "py-10" : ""}`}
        data-testid="inquiry-success"
      >
        <CheckCircle className={`w-12 h-12 mb-4 ${dark ? "text-[#C8A23A]" : "text-green-500"}`} />
        <h3 className={`text-xl mb-2 ${dark ? "text-white" : "text-[#1A1A1A]"}`} style={{ fontFamily: "'Playfair Display', serif" }}>Thank You!</h3>
        <p className={`text-sm ${dark ? "text-white/60" : "text-[#6B7280]"}`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
          We've received your inquiry. Our team will get back to you shortly.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`inquiry-form-${source}`} className="space-y-4">
      <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2"} gap-4`}>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Full Name *</label>
          <input type="text" required value={form.name} onChange={update("name")} placeholder="Your name" className={ic} data-testid="inquiry-name" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Phone *</label>
          <input type="tel" required value={form.phone} onChange={update("phone")} placeholder="+91 XXXXX XXXXX" className={ic} data-testid="inquiry-phone" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Email *</label>
          <input type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" className={ic} data-testid="inquiry-email" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Company</label>
          <input type="text" value={form.company} onChange={update("company")} placeholder="Company name" className={ic} data-testid="inquiry-company" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Location</label>
          <input type="text" value={form.location} onChange={update("location")} placeholder="City / State" className={ic} data-testid="inquiry-location" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Designation</label>
          <input type="text" value={form.designation} onChange={update("designation")} placeholder="Your role" className={ic} data-testid="inquiry-designation" style={{ fontFamily: "'Outfit', sans-serif" }} />
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Quantity</label>
          <select value={form.quantity} onChange={update("quantity")} className={ic} data-testid="inquiry-quantity" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <option value="">Select quantity</option>
            {QUANTITY_OPTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
        <div>
          <label className={lc} style={{ fontFamily: "'Outfit', sans-serif" }}>Category</label>
          <select value={form.category} onChange={update("category")} className={ic} data-testid="inquiry-category" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <option value="">Select category</option>
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={status === "loading"}
        data-testid="inquiry-submit"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`w-full flex items-center justify-center gap-2 py-4 rounded-full text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
          dark
            ? "bg-[#C8A23A] text-[#1A1A1A] hover:bg-[#D4B04A] shadow-lg shadow-[#C8A23A]/20"
            : "bg-[#7A1A31] text-white hover:bg-[#5C162E] shadow-lg shadow-[#7A1A31]/15"
        } disabled:opacity-50`}
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {status === "loading" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>Send Inquiry <Send className="w-4 h-4" /></>
        )}
      </motion.button>

      {status === "error" && (
        <p className="text-center text-sm text-red-500" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Something went wrong. Please try again or contact us on WhatsApp.
        </p>
      )}
    </form>
  );
};
